import { scaleBand, scaleLinear } from 'd3';
import { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { Family, FamilyOccurrence, Relation } from './models/Discovery';
import './viz.css';

/**
 * How a pattern family moves through the piece: its entries per voice over time, the transitions between consecutive
 * entries (voice change and pitch-level change), and the distributions of its entries.
 *
 * Colour encodes three relation classes (validated categorical slots 1-3, see docs/UI.md); the specific relation is
 * written on the mark (secondary encoding), listed in the legend and in the table below.
 */

type RelationClass = 'same' | 'transposed' | 'changed';

const relationClasses: RelationClass[] = ['same', 'transposed', 'changed'];

const classOf = (relation: Relation): RelationClass =>
  relation === 'prototype' || relation === 'exact'
    ? 'same'
    : relation === 'transposition'
      ? 'transposed'
      : 'changed';

const signed = (value: number | null | undefined) =>
  value === null || value === undefined
    ? ''
    : value > 0
      ? `+${value}`
      : value < 0
        ? `−${-value}`
        : '±0';

const factorGlyph = (factor: string | null) => {
  if (!factor) return '';
  const [numerator, denominator] = factor.split('/').map(Number);
  if (denominator === 2 && numerator === 1) return '×½';
  return denominator ? `×${numerator}/${denominator}` : `×${numerator}`;
};

const readablePitch = (pitch: string) => pitch.replace('-', '♭').replace('#', '♯');

/** The short text written on an entry's mark. */
export const glyph = (occurrence: FamilyOccurrence, translate: TFunction) => {
  switch (occurrence.relation) {
    case 'prototype':
      return translate('family.glyphs.prototype');
    case 'exact':
      return occurrence.semitones ? `= ${signed(occurrence.semitones)}` : '=';
    case 'transposition':
      return `${signed(occurrence.semitones)}${occurrence.tonal ? ` ${translate('family.glyphs.tonal')}` : ''}`;
    case 'variant':
      return `≈ ${signed(occurrence.semitones)}`;
    case 'inversion':
      return `${translate('family.glyphs.inversion')} ${signed(occurrence.semitones)}`;
    case 'retrograde':
      return `${translate('family.glyphs.retrograde')} ${signed(occurrence.semitones)}`;
    case 'augmentation':
    case 'diminution':
      return factorGlyph(occurrence.factor);
  }
};

/** How an entry relates to the first entry, e.g. "tonal transposition, −5 semitones". */
export const relationOf = (occurrence: FamilyOccurrence, translate: TFunction) => {
  const shift = occurrence.semitones
    ? `, ${translate('family.semitones', { shift: signed(occurrence.semitones) })}`
    : '';
  switch (occurrence.relation) {
    case 'prototype':
      return translate('family.relations.prototype');
    case 'exact':
      return translate('family.relations.exact') + shift;
    case 'transposition':
      return (
        translate(occurrence.tonal ? 'family.relations.tonal' : 'family.relations.real') + shift
      );
    case 'variant':
      return translate('family.relations.variant', { count: occurrence.mismatches }) + shift;
    case 'inversion':
      return translate('family.relations.inversion') + shift;
    case 'retrograde':
      return translate('family.relations.retrograde') + shift;
    case 'augmentation':
    case 'diminution':
      return `${translate(`family.relations.${occurrence.relation}`)} ${factorGlyph(occurrence.factor)}`;
  }
};

/** A sentence describing the entry, for tooltips and screen readers. */
export const describe = (occurrence: FamilyOccurrence, translate: TFunction) =>
  `${translate('family.where', {
    bars:
      occurrence.endMeasure !== occurrence.measure
        ? `${occurrence.measure}–${occurrence.endMeasure}`
        : `${occurrence.measure}`,
    pitch: occurrence.startPitch ? readablePitch(occurrence.startPitch) : translate('family.rest'),
    voice: occurrence.voiceNumber
  })}: ${relationOf(occurrence, translate)}`;

const laneHeight = 28;
const markHeight = 18;
const labelWidth = 64;
const arcSpace = 26;

const TransitionStrip = ({
  family,
  hoveredIndex,
  lastOffset,
  onHover,
  onSelect,
  voices
}: {
  family: Family;
  hoveredIndex: number | undefined;
  lastOffset: number;
  onHover: (index: number | undefined) => void;
  onSelect: (index: number) => void;
  voices: number[];
}) => {
  const { t: translate } = useTranslation();
  const [reference, { width }] = useMeasure<HTMLDivElement>();
  const plotWidth = Math.max(width - labelWidth - 8, 120);
  const x = scaleLinear()
    .domain([0, lastOffset])
    .range([labelWidth, labelWidth + plotWidth]);
  const lane = scaleBand<number>()
    .domain(voices)
    .range([arcSpace, arcSpace + voices.length * laneHeight]);
  const height = arcSpace + voices.length * laneHeight + 22;
  const showTransitionLabels = family.transitions.length <= 12;

  const centre = (occurrence: FamilyOccurrence) => ({
    x: x(occurrence.firstOffset),
    y: (lane(occurrence.voiceNumber) ?? 0) + laneHeight / 2
  });

  return (
    <div className="w-full" ref={reference}>
      <svg
        aria-label={translate('family.strip', { label: family.label })}
        className="block"
        height={height}
        role="img"
        width={width || 400}
      >
        {voices.map((voice) => (
          <g key={voice}>
            <line
              stroke="var(--viz-grid)"
              strokeWidth={1}
              x1={labelWidth}
              x2={labelWidth + plotWidth}
              y1={(lane(voice) ?? 0) + laneHeight / 2}
              y2={(lane(voice) ?? 0) + laneHeight / 2}
            />
            <text
              dominantBaseline="middle"
              fill="var(--viz-text-secondary)"
              fontSize={12}
              x={0}
              y={(lane(voice) ?? 0) + laneHeight / 2}
            >
              {translate('family.voice', { voice })}
            </text>
          </g>
        ))}

        {family.transitions.map((transition) => {
          const from = centre(family.occurrences[transition.from]);
          const to = centre(family.occurrences[transition.to]);
          const top = Math.min(from.y, to.y) - laneHeight / 2 - 8;
          const isActive = hoveredIndex === transition.from || hoveredIndex === transition.to;

          return (
            <g key={`${transition.from}-${transition.to}`}>
              <path
                d={`M ${from.x} ${from.y - markHeight / 2} C ${from.x} ${top}, ${to.x} ${top}, ${to.x} ${
                  to.y - markHeight / 2
                }`}
                fill="none"
                stroke={isActive ? 'var(--viz-text-primary)' : 'var(--viz-axis)'}
                strokeLinecap="round"
                strokeWidth={isActive ? 2 : 1.5}
              />
              {(showTransitionLabels || isActive) && transition.semitones !== null && (
                <text
                  fill="var(--viz-text-secondary)"
                  fontSize={11}
                  textAnchor="middle"
                  x={(from.x + to.x) / 2}
                  y={top + 2}
                >
                  {signed(transition.semitones)}
                </text>
              )}
            </g>
          );
        })}

        {family.occurrences.map((occurrence, index) => {
          const start = x(occurrence.firstOffset);
          const markWidth = Math.max(x(occurrence.endOffset) - start, 6);
          const y = (lane(occurrence.voiceNumber) ?? 0) + (laneHeight - markHeight) / 2;
          const text = glyph(occurrence, translate);
          const fitsText = markWidth >= text.length * 6.5 + 8;
          const isHovered = hoveredIndex === index;

          return (
            <g
              className="cursor-pointer"
              key={index}
              onClick={() => onSelect(index)}
              onFocus={() => onHover(index)}
              onBlur={() => onHover(undefined)}
              onKeyDown={(event) => (event.key === 'Enter' || event.key === ' ') && onSelect(index)}
              onPointerEnter={() => onHover(index)}
              onPointerLeave={() => onHover(undefined)}
              tabIndex={0}
            >
              <title>{describe(occurrence, translate)}</title>
              {/* The hit target is larger than the mark. */}
              <rect
                fill="transparent"
                height={laneHeight}
                width={markWidth + 4}
                x={start - 2}
                y={y - 5}
              />
              <rect
                fill={`var(--viz-relation-${classOf(occurrence.relation)})`}
                height={markHeight}
                opacity={hoveredIndex === undefined || isHovered ? 1 : 0.45}
                rx={4}
                stroke={isHovered ? 'var(--viz-text-primary)' : 'var(--viz-surface)'}
                strokeWidth={2}
                width={markWidth}
                x={start}
                y={y}
              />
              {fitsText && (
                <text
                  dominantBaseline="middle"
                  fill={`var(--viz-on-relation-${classOf(occurrence.relation)})`}
                  fontSize={11}
                  fontWeight={600}
                  pointerEvents="none"
                  x={start + 4}
                  y={y + markHeight / 2 + 0.5}
                >
                  {text}
                </text>
              )}
              <text
                fill="var(--viz-text-muted)"
                fontSize={10}
                pointerEvents="none"
                x={start}
                y={arcSpace + voices.length * laneHeight + 14}
              >
                {index === 0 || family.occurrences[index - 1].measure !== occurrence.measure
                  ? occurrence.measure
                  : ''}
              </text>
            </g>
          );
        })}
      </svg>
      <ul
        className="m-0 mt-1 flex flex-wrap gap-x-4 gap-y-1 p-0 text-xs"
        style={{ listStyle: 'none' }}
      >
        {relationClasses.map((relationClass) => (
          <li
            className="flex items-center gap-1.5"
            key={relationClass}
            style={{ color: 'var(--viz-text-secondary)' }}
          >
            <span
              aria-hidden
              className="inline-block h-2.5 w-3.5 rounded-sm"
              style={{ background: `var(--viz-relation-${relationClass})` }}
            />
            {translate(`family.classes.${relationClass}`)}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** A small single-series bar chart; the title names the series, so there is no legend. */
const Distribution = ({ entries, title }: { entries: [string, number][]; title: string }) => {
  const [reference, { width }] = useMeasure<HTMLDivElement>();
  const chartHeight = 72;
  const maximum = Math.max(...entries.map(([, value]) => value), 1);
  const band = scaleBand<string>()
    .domain(entries.map(([key]) => key))
    .range([0, width || 160])
    .paddingInner(0.25);
  const barWidth = Math.min(band.bandwidth(), 24);
  const y = scaleLinear()
    .domain([0, maximum])
    .range([0, chartHeight - 16]);

  return (
    <figure className="m-0 min-w-0" ref={reference}>
      <figcaption
        className="mb-1 text-xs font-semibold"
        style={{ color: 'var(--viz-text-primary)' }}
      >
        {title}
      </figcaption>
      <svg aria-label={title} height={chartHeight + 16} role="img" width={width || 160}>
        <line
          stroke="var(--viz-axis)"
          strokeWidth={1}
          x1={0}
          x2={width || 160}
          y1={chartHeight}
          y2={chartHeight}
        />
        {entries.map(([key, value]) => {
          const barHeight = y(value);
          const left = (band(key) ?? 0) + (band.bandwidth() - barWidth) / 2;

          return (
            <g key={key}>
              <title>{`${key}: ${value}`}</title>
              <path
                d={`M ${left} ${chartHeight} v ${-barHeight + 4} q 0 -4 4 -4 h ${barWidth - 8} q 4 0 4 4 v ${barHeight - 4} z`}
                fill="var(--viz-series-1)"
              />
              <text
                fill="var(--viz-text-secondary)"
                fontSize={10}
                textAnchor="middle"
                x={left + barWidth / 2}
                y={chartHeight - barHeight - 3}
              >
                {value}
              </text>
              <text
                fill="var(--viz-text-muted)"
                fontSize={10}
                textAnchor="middle"
                x={left + barWidth / 2}
                y={chartHeight + 12}
              >
                {key}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
};

const pitchClassOrder = ['C', 'C#', 'D', 'E-', 'E', 'F', 'F#', 'G', 'A-', 'A', 'B-', 'B'];

const FamilyDetail = ({
  family,
  lastOffset,
  onHoverOccurrence,
  onPlayOccurrence,
  onSelectOccurrence,
  voices
}: {
  family: Family;
  lastOffset: number;
  onHoverOccurrence: (occurrence: FamilyOccurrence | undefined) => void;
  onPlayOccurrence?: (occurrence: FamilyOccurrence) => void;
  onSelectOccurrence: (occurrence: FamilyOccurrence) => void;
  voices: number[];
}) => {
  const { t: translate } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number>();
  const hover = (index: number | undefined) => {
    setHoveredIndex(index);
    onHoverOccurrence(index === undefined ? undefined : family.occurrences[index]);
  };

  const counts = useMemo(() => {
    const byClass: Record<RelationClass, number> = { same: 0, transposed: 0, changed: 0 };
    family.occurrences.forEach((occurrence) => (byClass[classOf(occurrence.relation)] += 1));
    return byClass;
  }, [family]);
  const voicesUsed = Object.keys(family.distributions.byVoice).length;

  return (
    <section
      aria-label={translate('family.section', { label: family.label })}
      className="melodyvis-viz flex flex-col gap-3"
    >
      <p className="m-0 text-sm" style={{ color: 'var(--viz-text-secondary)' }}>
        {translate('family.summary', {
          changed: counts.changed,
          entries: translate('family.entries', { count: family.occurrences.length }),
          first: family.occurrences[0]?.measure,
          last: family.occurrences[family.occurrences.length - 1]?.endMeasure,
          same: counts.same,
          transposed: counts.transposed,
          voices: translate('family.voices', { count: voicesUsed })
        })}
        {family.sequences.length > 0 &&
          ` ${translate('family.sequences', { count: family.sequences.length })}`}
        {family.imitations.length > 0 &&
          ` ${translate('family.imitations', { count: family.imitations.length })}`}
      </p>

      <TransitionStrip
        family={family}
        hoveredIndex={hoveredIndex}
        lastOffset={lastOffset}
        onHover={hover}
        onSelect={(index) => onSelectOccurrence(family.occurrences[index])}
        voices={voices}
      />

      {/* The charts share their baseline even when a title wraps. */}
      <div
        className="grid items-end gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
      >
        <Distribution
          entries={voices.map((voice) => [
            translate('family.voiceShort', { voice }),
            family.distributions.byVoice[voice] ?? 0
          ])}
          title={translate('family.byVoice')}
        />
        <Distribution
          entries={Object.entries(family.distributions.bySemitones)
            .map(([semitones, count]): [number, number] => [Number(semitones), count])
            .sort(([first], [second]) => first - second)
            .map(([semitones, count]) => [signed(semitones), count])}
          title={translate('family.bySemitones')}
        />
        <Distribution
          entries={pitchClassOrder
            .filter((pitchClass) => family.distributions.byStartPitchClass[pitchClass])
            .map((pitchClass) => [
              readablePitch(pitchClass),
              family.distributions.byStartPitchClass[pitchClass]
            ])}
          title={translate('family.byStart')}
        />
      </div>

      <table
        className="w-full border-collapse text-xs"
        style={{ color: 'var(--viz-text-primary)' }}
      >
        <caption className="sr-only">{translate('family.table', { label: family.label })}</caption>
        <thead>
          <tr style={{ color: 'var(--viz-text-muted)' }}>
            <th className="whitespace-nowrap py-1 pr-2 text-left font-normal">#</th>
            <th className="whitespace-nowrap py-1 pr-2 text-left font-normal">
              {translate('family.columns.voice')}
            </th>
            <th className="whitespace-nowrap py-1 pr-2 text-left font-normal">
              {translate('family.columns.bars')}
            </th>
            <th className="whitespace-nowrap py-1 pr-2 text-left font-normal">
              {translate('family.columns.start')}
            </th>
            <th className="whitespace-nowrap py-1 pr-2 text-left font-normal">
              {translate('family.columns.relation')}
            </th>
            {onPlayOccurrence && (
              <th className="py-1 font-normal">
                <span className="sr-only">{translate('family.columns.play')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {family.occurrences.map((occurrence, index) => (
            <tr
              className="cursor-pointer"
              key={index}
              onClick={() => onSelectOccurrence(occurrence)}
              onPointerEnter={() => hover(index)}
              onPointerLeave={() => hover(undefined)}
              style={{ background: hoveredIndex === index ? 'var(--viz-hover)' : undefined }}
            >
              <td
                className="whitespace-nowrap py-0.5 pr-2 align-top"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {index + 1}
              </td>
              <td className="whitespace-nowrap py-0.5 pr-2 align-top">{occurrence.voiceNumber}</td>
              <td
                className="whitespace-nowrap py-0.5 pr-2 align-top"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {occurrence.measure}
                {occurrence.endMeasure !== occurrence.measure ? `–${occurrence.endMeasure}` : ''}
              </td>
              <td className="whitespace-nowrap py-0.5 pr-2 align-top">
                {occurrence.startPitch ? readablePitch(occurrence.startPitch) : '–'}
              </td>
              <td className="py-0.5 pr-2 align-top">
                <span
                  aria-hidden
                  className="mr-1.5 inline-block h-2 w-2.5 rounded-sm align-middle"
                  style={{ background: `var(--viz-relation-${classOf(occurrence.relation)})` }}
                />
                {relationOf(occurrence, translate)}
              </td>
              {onPlayOccurrence && (
                <td className="py-0.5 text-right">
                  <button
                    aria-label={translate('family.playEntry', { index: index + 1 })}
                    className="cursor-pointer rounded border border-solid border-slate-300 bg-white px-1.5 text-xs leading-5 hover:border-slate-500"
                    onClick={(event) => {
                      event.stopPropagation();
                      onPlayOccurrence(occurrence);
                    }}
                    type="button"
                  >
                    ▶
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {onPlayOccurrence && (
        <p className="m-0 text-xs text-slate-600">
          {translate('audio.credit')}{' '}
          <a href="http://creativecommons.org/licenses/by/3.0/" rel="noreferrer" target="_blank">
            CC BY 3.0
          </a>
        </p>
      )}
    </section>
  );
};

export default FamilyDetail;
